import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './AppSidebar.css';
import { Text, ProgressCircle, ActionButton } from '@react-spectrum/s2';
import { ConfirmDialog, ToolbarSearchField } from '../common';
import { AddDomainMenu } from '../common/AddDomainMenu';
import { Site, ConversationSummary, hasPermission, Permission } from '../../types';
import { Chat as S2Chat, History as S2History, Upload as S2Upload, Asset as S2Asset, ChartTrend as S2ChartTrend, Settings as S2Settings, Publish as S2Publish, Delete as S2Delete, Home as S2Home, Project as S2Project, ChevronRight as S2ChevronRight, ChevronLeft as S2ChevronLeft, UserSettings as S2UserSettings, Download as S2Download, HelpCircle as S2HelpCircle, Layers as S2Layers, Layout as S2Layout, New as NewChatIcon } from '../icons/s2';
import { DomainMonogram } from './DomainMonogram';
import { ROUTES } from '../../constants';
import { fetchRecentConversations, SiteDeletionConflictError, OrchestratorRef } from '../../services/api';
import { formatChatHistoryRelativeTime } from '../../utils/dateUtils';
import { groupConversationsByRecency } from '../../utils/chatHistoryBuckets';
import { resolveSettingsTab, SettingsTab } from '../../utils/settingsTabs';
import { useResizablePanel } from '../../hooks/useResizablePanel';
import { useIsMobile } from '../../hooks/useIsMobile';
// S2 icons return ReactNode, which this project's JSX typings reject; the shared
// adapter (../icons/s2) wraps them as v3-compatible components.
// S2 (this version) ships neither Wrench nor ArrowLeft; outline 20px stand-ins
// matching Figma's S2_Icon_Wrench_20_N / ArrowBack.
const WrenchIcon: React.FC = ()=>(<svg className="stroke-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
    <path d="M12.6 3.2a4.25 4.25 0 0 0-4.9 5.7l-4.6 4.6a1.6 1.6 0 0 0 2.3 2.3l4.6-4.6a4.25 4.25 0 0 0 5.7-4.9l-2.5 2.5-2.3-.6-.6-2.3 2.3-2.7Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>);
// Back arrow; outline back arrow matching Figma's S2 ArrowBack (20px).
const BackArrowIcon: React.FC = ()=>(<svg className="stroke-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
    <path d="M16.5 10H3.75M8.75 4.75 3.5 10l5.25 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>);
/** Optimistic pendingView sentinel for the domain Overview row (its route has no view). */ const DOMAIN_OVERVIEW_VIEW = 'domainOverview';
/** Primary rail (nav) — kept narrower than second-level list panels */ const PRIMARY_SIDEBAR_DEFAULT = 220;
/** Chat history + Domains listing — wider default for titles */ const SECONDARY_PANEL_DEFAULT = 300;
/** Domains drawer — Figma "Mainline" panel width. */ const DOMAINS_PANEL_DEFAULT = 280;
/** Chat history drawer — Figma 726:422063 panel width. */ const HISTORY_PANEL_DEFAULT = 360;
const SECONDARY_PANEL_MIN = 200;
const SECONDARY_PANEL_MAX = 480;
/* Ordered alphabetically by label */ const TOOLS_SECONDARY_ITEMS = [
    {
        id: 'entityDedup'
    },
    {
        id: 'evaluations'
    },
    {
        id: 'promptManager'
    }
] as const;
interface SettingsSecondaryItem {
    id: SettingsTab;
    /** When set, only show this entry for the matching domain_type. */ domainType?: 'standard' | 'orchestrator';
}
const SETTINGS_SECONDARY_ITEMS: SettingsSecondaryItem[] = [
    {
        id: 'accessControl'
    },
    {
        id: 'agentCard'
    },
    {
        id: 'apikeys'
    },
    {
        id: 'cache'
    },
    {
        id: 'widget'
    },
    {
        id: 'contentFilters'
    },
    {
        id: 'mcp'
    },
    {
        id: 'model'
    },
    {
        id: 'sources',
        domainType: 'orchestrator'
    }
];
/**
 * Orchestrator domains in `sites` that list `siteId` among their sources.
 * Orchestrators reference members via a flat `source_site_ids` array (no graph
 * edge), so membership is derived client-side from the already-loaded list.
 */ const findReferencingOrchestrators = (sites: Site[], siteId: string): OrchestratorRef[] =>sites.filter((s)=>s.domain_type === 'orchestrator' && (s.source_site_ids ?? []).includes(siteId)).map((s)=>({
            site_id: s.site_id,
            site_name: s.site_name
        }));
/* ------------------------------------------------------------------ */ /*  DomainListItem — single row in the domain list                     */ /* ------------------------------------------------------------------ */ interface DomainListItemProps {
    site: Site;
    onSelect: (site: Site) => void;
}
const DomainListItem: React.FC<DomainListItemProps> = ({ site, onSelect })=>{
    const { t } = useTranslation();
    const isDeleting = site.status === 'deleting';
    return (<button type="button" className="context-row" onClick={()=>onSelect(site)} style={isDeleting ? {
        opacity: 0.55
    } : undefined} aria-label={site.site_name}>
      <div className="context-name-wrap">
        {}
        <span className="project-icon-wrap">
          {isDeleting ? (<ProgressCircle aria-label={t('chat.sidebar.deletingProgress')} isIndeterminate size="S"/>) : (<DomainMonogram name={site.site_name} seed={site.site_id}/>)}
        </span>
        <span className="context-name" title={site.site_name}>
          {site.site_name}
          {isDeleting && <span className="context-deleting-label">{t('chat.sidebar.deleting')}</span>}
        </span>
      </div>
    </button>);
};
/* ------------------------------------------------------------------ */ /*  DomainTypeTabs — Standard / Orchestrators filter tabs              */ /* ------------------------------------------------------------------ */ type DomainTab = 'standard' | 'orchestrator';
const DOMAIN_TABS: ReadonlyArray<{
    id: DomainTab;
}> = [
    {
        id: 'standard'
    },
    {
        id: 'orchestrator'
    }
];
interface DomainTypeTabsProps {
    activeTab: DomainTab;
    onChange: (tab: DomainTab) => void;
}
/** Roving-tabindex tablist; the panel below filters its rows by the active tab. */ const DomainTypeTabs: React.FC<DomainTypeTabsProps> = ({ activeTab, onChange })=>{
    const { t } = useTranslation();
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>)=>{
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const index = DOMAIN_TABS.findIndex((tab)=>tab.id === activeTab);
        const delta = e.key === 'ArrowRight' ? 1 : DOMAIN_TABS.length - 1;
        onChange(DOMAIN_TABS[(index + delta) % DOMAIN_TABS.length].id);
    };
    return (<div className="domain-type-tabs" role="tablist" aria-label={t('chat.sidebar.domainTypeTabsLabel')} onKeyDown={handleKeyDown}>
      {DOMAIN_TABS.map((tab)=>(<button key={tab.id} type="button" role="tab" id={`domain-tab-${tab.id}`} aria-selected={activeTab === tab.id} aria-controls="domain-tabpanel" tabIndex={activeTab === tab.id ? 0 : -1} className="domain-type-tab" onClick={()=>onChange(tab.id)}>
          {t(`chat.sidebar.domainTabs.${tab.id}`)}
        </button>))}
    </div>);
};
/* ------------------------------------------------------------------ */ /*  Component                                                          */ /* ------------------------------------------------------------------ */ export interface AppSidebarProps {
    sites: Site[];
    loading: boolean;
    error: string | null;
    selectedSite: Site | null;
    activeView: 'chat' | 'edit' | 'addContent' | 'stats' | 'analytics' | 'create' | 'manageUsers' | 'tools' | 'history' | 'settings' | 'publishing' | 'security' | 'sampleContent' | 'documentation' | 'orgModels' | null;
    /** Current user's effective permissions for the selected site.
   *  undefined = still loading, null = no permissions / error. */ userPermissions: string[] | null | undefined;
    activeConversationId?: string | null;
    orgPermissions?: string[];
    /** True when the org has the publishing feature enabled (org-level flag). */ orgPublishingEnabled?: boolean;
    onSelectContext: (site: Site) => void;
    onBack: () => void;
    onNewChat: () => void;
    onStartChat: () => void;
    onDelete: (site: Site, force?: boolean) => void | Promise<void>;
    onAddContent: () => void;
    onStats: () => void;
    onAnalytics: () => void;
    onOpenCreate: (domainType?: 'standard' | 'orchestrator') => void;
    onSettings: () => void;
    onPublishing: () => void;
    onTools: () => void;
    onHistory: () => void;
    onResumeChat: (conversationId: string) => void;
    onSecurity?: () => void;
    onSampleContent?: () => void;
    onDocumentation?: () => void;
    onModels?: () => void;
    onClearOrgView?: () => void;
    /** True while the mobile off-canvas drawer is open (App.tsx's `navOpen`). Used
   *  to reset the one-level mobile nav back to its default context on every
   *  fresh open, so re-opening the drawer is predictable. */ isDrawerOpen?: boolean;
    /** Closes the mobile drawer. Selecting a leaf tab inside a secondary panel
   *  (Tools/Settings/Publishing) doesn't change the route's activeView, so it
   *  isn't already covered by App.tsx's "close drawer on destination change"
   *  effect — call this explicitly so the chosen tab's content becomes visible. */ onMobileNavClose?: () => void;
    /** Desktop-only: when true, render the primary rail as a compact icon-only
   *  strip (labels hidden visually but kept in the a11y tree). Ignored on mobile,
   *  where the off-canvas drawer always shows full labels. Persisted by the shell. */ collapsed?: boolean;
}
export const AppSidebar: React.FC<AppSidebarProps> = ({ sites, loading, error, selectedSite, activeView, userPermissions, activeConversationId, orgPermissions, orgPublishingEnabled, onSelectContext, onBack, onNewChat, onStartChat, onDelete, onAddContent, onStats, onAnalytics, onOpenCreate, onSettings, onPublishing, onTools, onHistory, onResumeChat, onSecurity, onSampleContent, onDocumentation, onModels, onClearOrgView, isDrawerOpen, onMobileNavClose, collapsed = false })=>{
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // At the mobile breakpoint the sidebar is an off-canvas drawer of limited
    // width; the desktop resizable pixel widths would overflow it, so we let CSS
    // size the primary rail + secondary panel instead of the inline widths.
    const isMobile = useIsMobile();
    // Collapse is a desktop-only affordance; on mobile the drawer always shows
    // full labels, so ignore the flag there entirely.
    const collapsedDesktop = collapsed && !isMobile;
    const [pendingDelete, setPendingDelete] = useState<{
        site: Site;
        orchestrators: OrchestratorRef[];
    } | null>(null);
    const [pendingView, setPendingView] = useState<string | null>(null);
    const [activeDomainTab, setActiveDomainTab] = useState<DomainTab>('standard');
    useEffect(()=>{
        setPendingView(null);
    }, [
        activeView
    ]);
    const effectiveView = pendingView ?? activeView;
    const { width: sidebarWidth, isDragging, onResizeStart: handleResizeStart } = useResizablePanel({
        minWidth: SECONDARY_PANEL_MIN,
        maxWidth: 420,
        defaultWidth: PRIMARY_SIDEBAR_DEFAULT,
        direction: 'right'
    });
    const { width: workspacesListWidth, isDragging: isWorkspacesListDragging, onResizeStart: handleWorkspacesListResizeStart } = useResizablePanel({
        minWidth: SECONDARY_PANEL_MIN,
        maxWidth: SECONDARY_PANEL_MAX,
        defaultWidth: DOMAINS_PANEL_DEFAULT,
        direction: 'right'
    });
    const { width: historySidebarWidth, isDragging: isHistoryDragging, onResizeStart: handleHistoryResizeStart } = useResizablePanel({
        minWidth: SECONDARY_PANEL_MIN,
        maxWidth: SECONDARY_PANEL_MAX,
        defaultWidth: SECONDARY_PANEL_DEFAULT,
        direction: 'right'
    });
    const { width: chatHistoryWidth, isDragging: isChatHistoryDragging, onResizeStart: handleChatHistoryResizeStart } = useResizablePanel({
        minWidth: SECONDARY_PANEL_MIN,
        maxWidth: SECONDARY_PANEL_MAX,
        defaultWidth: HISTORY_PANEL_DEFAULT,
        direction: 'right'
    });
    // ---- Workspaces secondary panel: closed by default so a fresh load lands on
    // the Overview dashboard (MainContent's no-site fallback). The "Domains" nav
    // button still toggles this panel open, and returning from a workspace
    // re-opens it (see the isInWorkspace effect below). ----
    const [workspacesOpen, setWorkspacesOpen] = useState(false);
    const [domainSearch, setDomainSearch] = useState('');
    // ---- One-level mobile nav ----
    // On the mobile drawer, the primary rail (icon menu) and a secondary panel
    // (Domains list / Tools / Settings / Chat history / Publishing) used to show
    // side by side and get squeezed unusably narrow. `mobileSecondaryDismissed`
    // lets the user "go back" to the primary rail (full width) without leaving
    // the secondary panel's underlying state (open/closed, selection, scroll)
    // untouched — tapping the nav item again (or reopening the drawer) shows the
    // secondary panel again. Desktop/tablet (>1024px, see App.css) render both
    // columns unconditionally and ignore this flag.
    const [mobileSecondaryDismissed, setMobileSecondaryDismissed] = useState(false);
    const showMobileSecondary = useCallback(()=>setMobileSecondaryDismissed(false), []);
    // Terminal nav items (those that navigate straight to a page instead of
    // opening a secondary panel) must close the mobile drawer themselves. Relying
    // only on App.tsx's "close on activeView change" leaves the drawer stuck open
    // whenever the tapped destination is already the current route (re-tapping the
    // active item), because navigate() to the same URL is a no-op. Closing here is
    // idempotent and only affects the mobile off-canvas drawer.
    const closeDrawerOnMobile = useCallback(()=>{
        if (isMobile) onMobileNavClose?.();
    }, [
        isMobile,
        onMobileNavClose
    ]);
    // Every time the drawer is freshly opened, default back to whichever level
    // the current route implies (secondary panel if one applies) rather than
    // remembering a stale "Back" tap from the previous time it was open.
    useEffect(()=>{
        if (isDrawerOpen) setMobileSecondaryDismissed(false);
    }, [
        isDrawerOpen
    ]);
    // ---- History panel state (data loading) ----
    const [historyChats, setHistoryChats] = useState<ConversationSummary[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    /** Second-level history list: opens strictly on ?history=true (resume from
   *  history sets this param too). A bare ?conversation=<id> loads that chat but
   *  must NOT auto-open the panel. */ const historyParam = searchParams.get('history') === 'true';
    const historyOpen = activeView === 'chat' && historyParam;
    const loadHistory = useCallback(async ()=>{
        if (!selectedSite) return;
        setHistoryLoading(true);
        try {
            const data = await fetchRecentConversations(selectedSite.site_id, selectedSite.org_id, 10);
            setHistoryChats(data);
        } catch  {
            setHistoryChats([]);
        } finally{
            setHistoryLoading(false);
        }
    }, [
        selectedSite
    ]);
    useEffect(()=>{
        if (historyOpen) loadHistory();
    }, [
        historyOpen,
        loadHistory
    ]);
    useEffect(()=>{
        setHistoryChats([]);
    }, [
        selectedSite?.site_id
    ]);
    useEffect(()=>{
        if (activeView === 'security' || activeView === 'sampleContent' || activeView === 'documentation') {
            setWorkspacesOpen(false);
        }
    }, [
        activeView
    ]);
    const [historySearch, setHistorySearch] = useState('');
    const historySections = useMemo(()=>{
        const query = historySearch.trim().toLowerCase();
        const chats = query ? historyChats.filter((c)=>c.first_question.toLowerCase().includes(query)) : historyChats;
        return groupConversationsByRecency(chats, t);
    }, [
        historyChats,
        historySearch,
        t
    ]);
    const handleConfirmDelete = async ()=>{
        if (!pendingDelete) return;
        const { site, orchestrators } = pendingDelete;
        setPendingDelete(null);
        try {
            // Force past the backend membership gate only when this domain is a known
            // orchestrator source — the single dialog has already warned the user.
            await onDelete(site, orchestrators.length > 0);
        } catch (err) {
            // Defensive: the loaded site list missed a membership the backend enforces
            // (e.g. stale data). Re-open the same dialog with the authoritative list so
            // the user still sees exactly one, correctly-worded confirmation.
            if (err instanceof SiteDeletionConflictError) {
                setPendingDelete({
                    site,
                    orchestrators: err.orchestrators
                });
            }
        }
    };
    const isInWorkspace = !!selectedSite;
    const isInChat = isInWorkspace && effectiveView === 'chat';
    const isDeleting = selectedSite?.status === 'deleting';
    /** Domains drawer open on an org page: Domains shows the Figma "expanded" (gray) state. */ const isDomainsActive = workspacesOpen && !isInWorkspace && !effectiveView;
    /** HOME renders the Overview dashboard (activeView null). It stays selected while the
   *  Domains drawer is open over it; any org-level view deselects it. */ const isOverviewActive = !isInWorkspace && !effectiveView;
    /** Domain landing page (/domains/:id, activeView null) is the domain's Overview. */ const isDomainOverviewActive = isInWorkspace && (!effectiveView || effectiveView === DOMAIN_OVERVIEW_VIEW);
    /** Treat missing domain_type as 'standard' so legacy sites keep their content nav. */ const domainType = selectedSite?.domain_type ?? 'standard';
    const isOrchestrator = domainType === 'orchestrator';
    // Only surface the Standard/Orchestrator tabs when at least one orchestrator
    // domain exists; otherwise there is nothing to switch to, so the plain
    // standard-domain list is shown. Falling back to 'standard' also keeps the
    // list correct if the last orchestrator is deleted while its tab was active.
    const hasOrchestratorDomains = useMemo(()=>sites.some((s)=>s.domain_type === 'orchestrator'), [
        sites
    ]);
    const effectiveDomainTab: DomainTab = hasOrchestratorDomains ? activeDomainTab : 'standard';
    const canUpdateSite = hasPermission(userPermissions, Permission.UPDATE_SITE);
    const canManageContent = hasPermission(userPermissions, Permission.MANAGE_CONTENT);
    // Publishing requires the org-level publishing flag plus site-scoped MANAGE_CONTENT
    // (mirrors the backend's publish-endpoint gate). Orchestrator domains have no
    // indexed content of their own, so publishing never applies to them.
    const canAccessPublishing = !isOrchestrator && !!orgPublishingEnabled && canManageContent;
    const toolsSecondaryOpen = isInWorkspace && effectiveView === 'tools' && canUpdateSite;
    const settingsSecondaryOpen = isInWorkspace && effectiveView === 'settings' && canUpdateSite;
    const publishingSecondaryOpen = isInWorkspace && effectiveView === 'publishing' && canAccessPublishing;
    /** True whenever ANY secondary panel is applicable for the current route —
   *  drives the one-level mobile nav (see `mobileSecondaryDismissed` above). */ const mobileSecondaryOpen = (workspacesOpen && !isInWorkspace) || (historyOpen && isInWorkspace) || toolsSecondaryOpen || settingsSecondaryOpen || publishingSecondaryOpen;
    const mobileShowingSecondary = mobileSecondaryOpen && !mobileSecondaryDismissed;
    const sidebarAreaMobileClass = !mobileSecondaryOpen ? '' : mobileShowingSecondary ? ' sidebar-area--mobile-secondary' : ' sidebar-area--mobile-primary';
    const toolsTabParam = searchParams.get('toolsTab') ?? 'entityDedup';
    const settingsTabParam = resolveSettingsTab(searchParams.get('settingsTab'));
    const publishingTab = searchParams.get('publishingTab') ?? 'maps';
    const visibleSettingsItems = useMemo(()=>SETTINGS_SECONDARY_ITEMS.filter((item)=>!item.domainType || item.domainType === domainType), [
        domainType
    ]);
    // Re-open workspaces panel when returning from a workspace (not on initial mount)
    const wasInWorkspaceRef = useRef(false);
    useEffect(()=>{
        if (isInWorkspace) {
            wasInWorkspaceRef.current = true;
        } else if (wasInWorkspaceRef.current) {
            wasInWorkspaceRef.current = false;
            setWorkspacesOpen(true);
        }
    }, [
        isInWorkspace
    ]);
    const getPanelTransform = (direction: 'left' | 'right', visible: boolean)=>{
        if (visible) return 'translateX(0)';
        return direction === 'left' ? 'translateX(-100%)' : 'translateX(100%)';
    };
    // Native hover tooltip for the icon-only collapsed rail. When the desktop rail
    // is collapsed the text labels are hidden (font-size:0 / display:none), so an
    // icon alone gives no hint of its destination — surface the name on hover. The
    // expanded rail and the mobile drawer already show the label, so the tooltip is
    // omitted there to avoid a redundant hover card.
    const railTitle = (label: string): string | undefined =>collapsedDesktop ? label : undefined;
    const renderFooterAction = (): React.ReactNode =>{
        if (userPermissions === undefined) return null;
        if (isDeleting) {
            return (<div className="action-row" style={{
                opacity: 0.5,
                cursor: 'default',
                pointerEvents: 'none'
            }}>
          <span className="action-icon-wrap">
            <ProgressCircle aria-label={t('chat.sidebar.deleting')} isIndeterminate size="S"/>
          </span>{' '}
          {t('chat.sidebar.deleting')}
        </div>);
        }
        if (!hasPermission(userPermissions, Permission.DELETE_SITE)) return null;
        return (<button type="button" className="action-row action-row--danger" onClick={()=>{
            if (selectedSite) {
                setPendingDelete({
                    site: selectedSite,
                    orchestrators: findReferencingOrchestrators(sites, selectedSite.site_id)
                });
            }
        }} aria-label={t('chat.sidebar.deleteDomainAria')} title={railTitle(t('chat.sidebar.deleteDomain'))}>
        <span className="action-icon-wrap action-icon-wrap--danger">
          <S2Delete/>
        </span>
        {t('chat.sidebar.deleteDomain')}
      </button>);
    };
    /** Shared Back affordance atop each mobile-only secondary panel; returns to
   *  the primary rail (icon menu) without closing the whole drawer. */ const renderMobileBackButton = (label: string): React.ReactNode =>(<ActionButton isQuiet onPress={()=>setMobileSecondaryDismissed(true)} aria-label={label} UNSAFE_className="secondary-panel-back-btn">
      <S2ChevronLeft UNSAFE_style={{
            width: 20,
            height: 20
        }}/>
    </ActionButton>);
    return (<div className={`sidebar-area${sidebarAreaMobileClass}${collapsedDesktop ? ' sidebar-area--collapsed' : ''}`} inert={isMobile && !isDrawerOpen}>
      {}
      <div className="sidebar-wrapper" style={isMobile || collapsedDesktop ? undefined : {
        width: sidebarWidth
    }}>
        <div className="sidebar-container">
          {}
          <div className="sidebar-panel sidebar-panel--nav" /* When parked off-screen (in a workspace) this org-nav panel is hidden
               visually (opacity:0) and to the mouse (pointerEvents:none) — inert also
               removes its buttons from the tab order / screen-reader tree so AT and
               keyboard users can't focus the off-screen global-nav items. */ inert={isInWorkspace} style={{
        transform: getPanelTransform('left', !isInWorkspace),
        opacity: !isInWorkspace ? 1 : 0,
        pointerEvents: !isInWorkspace ? 'auto' : 'none'
    }}>
            <div className="actions-list">
              <button type="button" className={`action-row ${isOverviewActive ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView(null);
        setWorkspacesOpen(false);
        onClearOrgView?.();
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.overview')} title={railTitle(t('chat.sidebar.overview'))} aria-current={isOverviewActive ? 'page' : undefined}>
                <span className="action-icon-wrap">
                  <S2Home/>
                </span>
                {t('chat.sidebar.overview')}
              </button>

              <button type="button" className={`action-row ${isDomainsActive ? 'action-row--expanded' : ''}`} onClick={()=>{
        setWorkspacesOpen((prev)=>!prev);
        onClearOrgView?.();
        showMobileSecondary();
    }} aria-label={t('chat.sidebar.domains')} title={railTitle(t('chat.sidebar.domains'))} aria-expanded={workspacesOpen} data-tour="domains-nav">
                <span className="action-icon-wrap">
                  <S2Project/>
                </span>
                <span className="action-row-label">{t('chat.sidebar.domains')}</span>
                {!loading && <span className="action-row-value">{sites.length}</span>}
                <span className="action-row-drill" aria-hidden>
                  <S2ChevronRight/>
                </span>
              </button>

              {(hasPermission(orgPermissions, Permission.MANAGE_USER) || hasPermission(orgPermissions, Permission.MANAGE_ORG)) && (<button type="button" className={`action-row ${effectiveView === 'security' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('security');
        setWorkspacesOpen(false);
        onSecurity?.();
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.users')} title={railTitle(t('chat.sidebar.users'))}>
                  <span className="action-icon-wrap">
                    <S2UserSettings/>
                  </span>
                  {t('chat.sidebar.users')}
                </button>)}


              {hasPermission(orgPermissions, Permission.MANAGE_ORG) && (<button type="button" className={`action-row ${effectiveView === 'orgModels' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('orgModels');
        setWorkspacesOpen(false);
        onModels?.();
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.models')} title={railTitle(t('chat.sidebar.models'))}>
                  <span className="action-icon-wrap">
                    <S2Layers/>
                  </span>
                  {t('chat.sidebar.models')}
                </button>)}

            </div>

            <div className="sidebar-footer">
              <button type="button" className={`action-row ${effectiveView === 'sampleContent' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('sampleContent');
        setWorkspacesOpen(false);
        onSampleContent?.();
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.sampleContent')} title={railTitle(t('chat.sidebar.sampleContent'))}>
                <span className="action-icon-wrap">
                  <S2Download/>
                </span>
                {t('chat.sidebar.sampleContent')}
              </button>

              <button type="button" className={`action-row ${effectiveView === 'documentation' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('documentation');
        setWorkspacesOpen(false);
        onDocumentation?.();
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.documentation')} title={railTitle(t('chat.sidebar.documentation'))}>
                <span className="action-icon-wrap">
                  <S2HelpCircle/>
                </span>
                {t('chat.sidebar.documentation')}
              </button>
            </div>
          </div>

          {}
          <div className="sidebar-panel sidebar-panel--nav" /* Symmetric to Panel 1: when the workspace nav is parked off-screen (not in
               a workspace) inert keeps its Back button + domain actions out of the tab
               order / AT tree. */ inert={!isInWorkspace} style={{
        transform: getPanelTransform('right', isInWorkspace),
        opacity: isInWorkspace ? 1 : 0,
        pointerEvents: isInWorkspace ? 'auto' : 'none'
    }}>
            <button type="button" className="action-row domain-back-row" onClick={onBack} aria-label={t('chat.sidebar.backToDomains')} title={selectedSite?.site_name}>
              <span className="action-icon-wrap">
                <BackArrowIcon/>
              </span>
              <span className="workspace-title">{selectedSite?.site_name}</span>
            </button>

            <div className="actions-list" style={isDeleting ? {
        opacity: 0.5,
        pointerEvents: 'none'
    } : undefined}>
              {userPermissions === undefined ? (<div className={" sd171 _Ca171 eb171 JC171 GC171"}>
                  <ProgressCircle aria-label={t('chat.sidebar.loadingActions')} isIndeterminate size="S"/>
                </div>) : (<>
                  {}
                  <button type="button" className={`action-row ${isDomainOverviewActive ? 'action-row--active' : ''}`} onClick={()=>{
        if (!selectedSite) return;
        setPendingView(DOMAIN_OVERVIEW_VIEW);
        navigate(`/domains/${selectedSite.site_id}`);
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.overview')} title={railTitle(t('chat.sidebar.overview'))} aria-current={isDomainOverviewActive ? 'page' : undefined}>
                    <span className="action-icon-wrap">
                      <S2Layout/>
                    </span>
                    {t('chat.sidebar.overview')}
                  </button>

                  <button type="button" className={`action-row ${effectiveView === 'chat' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('chat');
        (isInChat ? onNewChat : onStartChat)();
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.newChat')} title={railTitle(t('chat.sidebar.newChat'))}>
                    <span className="action-icon-wrap">
                      <S2Chat/>
                    </span>
                    {t('chat.sidebar.newChat')}
                  </button>

                  <button type="button" className={`action-row ${effectiveView === 'history' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('history');
        onHistory();
        showMobileSecondary();
    }} aria-label={t('chat.sidebar.chatHistory')} title={railTitle(t('chat.sidebar.chatHistory'))}>
                    <span className="action-icon-wrap">
                      <S2History/>
                    </span>
                    {t('chat.sidebar.chatHistory')}
                  </button>

                  {}
                  {!isOrchestrator && (<>
                      {hasPermission(userPermissions, Permission.MANAGE_CONTENT) && (<button type="button" className={`action-row ${effectiveView === 'addContent' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('addContent');
        onAddContent();
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.addContentAria')} title={railTitle(t('chat.sidebar.addContent'))}>
                          <span className="action-icon-wrap">
                            <S2Upload/>
                          </span>
                          {t('chat.sidebar.addContent')}
                        </button>)}

                      <button type="button" className={`action-row ${effectiveView === 'stats' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('stats');
        onStats();
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.content')} title={railTitle(t('chat.sidebar.content'))}>
                        <span className="action-icon-wrap">
                          <S2Asset/>
                        </span>
                        {t('chat.sidebar.content')}
                      </button>
                    </>)}

                  {}
                  {hasPermission(userPermissions, Permission.UPDATE_SITE) && (<button type="button" className={`action-row ${effectiveView === 'analytics' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('analytics');
        onAnalytics();
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.analytics')} title={railTitle(t('chat.sidebar.analytics'))}>
                      <span className="action-icon-wrap">
                        <S2ChartTrend/>
                      </span>
                      {t('chat.sidebar.analytics')}
                    </button>)}

                  {}
                  {hasPermission(userPermissions, Permission.UPDATE_SITE) && (<button type="button" className={`action-row ${effectiveView === 'tools' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('tools');
        onTools();
        showMobileSecondary();
    }} aria-label={t('chat.sidebar.tools')} title={railTitle(t('chat.sidebar.tools'))}>
                      <span className="action-icon-wrap">
                        <WrenchIcon/>
                      </span>
                      {t('chat.sidebar.tools')}
                    </button>)}

                  {}
                  {hasPermission(userPermissions, Permission.UPDATE_SITE) && (<button type="button" className={`action-row ${effectiveView === 'settings' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('settings');
        onSettings();
        showMobileSecondary();
    }} aria-label={t('chat.sidebar.settings')} title={railTitle(t('chat.sidebar.settings'))}>
                      <span className="action-icon-wrap">
                        <S2Settings/>
                      </span>
                      {t('chat.sidebar.settings')}
                    </button>)}

                  {}
                  {canAccessPublishing && (<button type="button" className={`action-row ${effectiveView === 'publishing' ? 'action-row--active' : ''}`} onClick={()=>{
        setPendingView('publishing');
        onPublishing();
        showMobileSecondary();
    }} aria-label={t('chat.sidebar.publishing')} title={railTitle(t('chat.sidebar.publishing'))}>
                      <span className="action-icon-wrap">
                        <S2Publish/>
                      </span>
                      {t('chat.sidebar.publishing')}
                    </button>)}
                </>)}
            </div>

            <div className="sidebar-footer">
              {renderFooterAction()}
            </div>
          </div>

          {}
          <ConfirmDialog isOpen={!!pendingDelete} onOpenChange={(open)=>{
        if (!open) setPendingDelete(null);
    }} title={t('chat.sidebar.deleteDomainTitle')} confirmLabel={t('primitives.delete')} onConfirm={handleConfirmDelete}>
            {pendingDelete && pendingDelete.orchestrators.length > 0 ? (<>
                {t('chat.sidebar.deleteOrchestratorIntro', {
        count: pendingDelete.orchestrators.length
    })}
                <ul className="delete-source-orchestrator-list">
                  {pendingDelete.orchestrators.map((o)=>(<li key={o.site_id}>{o.site_name}</li>))}
                </ul>
                {t('chat.sidebar.deleteOrchestratorOutro', {
        count: pendingDelete.orchestrators.length
    })}
              </>) : (t('chat.sidebar.deleteDomainBody'))}
          </ConfirmDialog>
        </div>
        <div className={`resize-handle ${isDragging ? 'resize-handle--dragging' : ''}`} onMouseDown={handleResizeStart} role="separator" aria-orientation="vertical"/>
      </div>

      {}
      {workspacesOpen && !isInWorkspace && (<div className="sidebar-secondary-shell" style={isMobile ? undefined : {
        width: workspacesListWidth
    }}>
          <div className="workspaces-sidebar workspaces-sidebar--section workspaces-sidebar--domains">
            <div className="workspaces-sidebar-header workspaces-sidebar-header--flush">
              <div className="secondary-panel-heading">
                {isMobile && renderMobileBackButton(t('chat.sidebar.backToMenu'))}
                <div className="workspaces-sidebar-title">{t('chat.sidebar.domains')}</div>
              </div>
              {hasPermission(orgPermissions, Permission.CREATE_SITE) && (<AddDomainMenu onOpenCreate={onOpenCreate} className="workspaces-sidebar-add-btn" data-tour="add-context-btn"/>)}
            </div>
            {hasOrchestratorDomains && (<DomainTypeTabs activeTab={activeDomainTab} onChange={setActiveDomainTab}/>)}
            <ToolbarSearchField aria-label={t('chat.sidebar.searchDomains')} value={domainSearch} onChange={setDomainSearch} fill/>
            <div className="workspaces-sidebar-list" data-tour="contexts-list" role={hasOrchestratorDomains ? 'tabpanel' : undefined} id={hasOrchestratorDomains ? 'domain-tabpanel' : undefined} aria-labelledby={hasOrchestratorDomains ? `domain-tab-${effectiveDomainTab}` : undefined}>
              {loading && (<div className={" sd171 _Ca171 eb171 JG171"}>
                  <ProgressCircle aria-label={t('chat.sidebar.loadingDomains')} isIndeterminate size="S"/>
                </div>)}
              {error && (<div className={" Tf171 Qf171 Sf171 Rf171"}>
                  <Text UNSAFE_style={{
        color: 'var(--spectrum-semantic-negative-color-default)'
    }}>
                    {error}
                  </Text>
                </div>)}
              {!loading && !error && (()=>{
        const query = domainSearch.trim().toLowerCase();
        const tabSites = sites.filter((s)=>effectiveDomainTab === 'orchestrator' ? s.domain_type === 'orchestrator' : (s.domain_type ?? 'standard') !== 'orchestrator');
        const visibleSites = query ? tabSites.filter((s)=>s.site_name.toLowerCase().includes(query)) : tabSites;
        if (query && visibleSites.length === 0 && tabSites.length > 0) {
            return (<div className={" Th171 Qh171 Sh171 Rh171"}>
                      <Text UNSAFE_style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-base)'
            }}>
                        {t('chat.sidebar.noDomainsMatch')}
                      </Text>
                    </div>);
        }
        if (visibleSites.length === 0) {
            return (<div className={" Th171 Qh171 Sh171 Rh171"}>
                      <Text UNSAFE_style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-base)'
            }}>
                        {effectiveDomainTab === 'orchestrator' ? t('chat.sidebar.noOrchestratorDomains') : t('chat.sidebar.noStandardDomains')}
                      </Text>
                    </div>);
        }
        return visibleSites.map((site)=>(<DomainListItem key={site.site_id} site={site} onSelect={onSelectContext}/>));
    })()}
            </div>
          </div>
          <div className={`resize-handle ${isWorkspacesListDragging ? 'resize-handle--dragging' : ''}`} onMouseDown={handleWorkspacesListResizeStart} role="separator" aria-orientation="vertical"/>
        </div>)}

      {}
      {historyOpen && isInWorkspace && (<div className="sidebar-secondary-shell" style={isMobile ? undefined : {
        width: chatHistoryWidth
    }}>
          <div className="history-sidebar history-sidebar--figma">
            <div className="history-sidebar-header">
              <div className="secondary-panel-heading">
                {isMobile && renderMobileBackButton(t('chat.sidebar.backToMenu'))}
                <h2 className="history-sidebar-heading">{t('chat.sidebar.chatHistory')}</h2>
              </div>
              <ActionButton UNSAFE_className="history-sidebar-new-chat-btn" onPress={()=>{
        onNewChat();
        if (selectedSite) {
            navigate(`/domains/${selectedSite.site_id}/chat?history=true`, {
                replace: true
            });
        }
        closeDrawerOnMobile();
    }} aria-label={t('chat.sidebar.newChat')}>
                <NewChatIcon/>
                <Text>{t('chat.sidebar.newChat')}</Text>
              </ActionButton>
            </div>
            <div className="history-sidebar-search">
              <ToolbarSearchField aria-label={t('chat.sidebar.searchChatHistory')} placeholder={t('chat.sidebar.searchPlaceholder')} value={historySearch} onChange={setHistorySearch} fill/>
            </div>
            <div className="history-sidebar-list">
              {historyLoading ? (<div className={" sd171 _Ca171 eb171 JG171"}>
                  <ProgressCircle aria-label={t('primitives.loading')} isIndeterminate size="S"/>
                </div>) : historyChats.length === 0 ? (<div className={" Th171 Qh171 Sh171 Rh171"}>
                  <Text UNSAFE_style={{
        color: 'var(--color-text-muted)',
        fontSize: 'var(--font-size-base)'
    }}>
                    {t('chat.sidebar.noConversations')}
                  </Text>
                </div>) : historySections.length === 0 ? (<div className={" Th171 Qh171 Sh171 Rh171"}>
                  <Text UNSAFE_style={{
        color: 'var(--color-text-muted)',
        fontSize: 'var(--font-size-base)'
    }}>
                    {t('chat.sidebar.noConversationsMatch')}
                  </Text>
                </div>) : (historySections.map((section)=>(<div key={section.bucketKey} className="history-sidebar-bucket">
                    <div className="history-sidebar-bucket__label" role="presentation">
                      {section.sectionLabel}
                    </div>
                    {section.items.map((chat)=>(<button type="button" key={chat.conversation_id} className={`history-chat-row ${activeConversationId === chat.conversation_id ? 'history-chat-row--active' : ''}`} onClick={()=>{
                onResumeChat(chat.conversation_id);
                closeDrawerOnMobile();
            }} title={chat.first_question} aria-label={`${section.sectionLabel}: ${chat.first_question}`}>
                        <div className="history-chat-body">
                          <div className="history-chat-title">
                            {chat.first_question.length > 72 ? chat.first_question.slice(0, 72) + '…' : chat.first_question}
                          </div>
                          <div className="history-chat-meta">
                            <span>{t('chat.sidebar.turnCount', {
                count: chat.turn_count
            })}</span>
                            {chat.updated_at && (<>
                                <span className="history-chat-meta__divider" aria-hidden/>
                                <span>{formatChatHistoryRelativeTime(chat.updated_at)}</span>
                              </>)}
                          </div>
                        </div>
                        <span className="history-chat-chevron" aria-hidden>
                          <S2ChevronRight/>
                        </span>
                      </button>))}
                  </div>)))}
            </div>
          </div>
          <div className={`resize-handle ${isChatHistoryDragging ? 'resize-handle--dragging' : ''}`} onMouseDown={handleChatHistoryResizeStart} role="separator" aria-orientation="vertical"/>
        </div>)}

      {}
      {toolsSecondaryOpen && selectedSite && (<div className="sidebar-secondary-shell" style={isMobile ? undefined : {
        width: historySidebarWidth
    }}>
          <div className="workspaces-sidebar workspaces-sidebar--section">
            <div className="workspaces-sidebar-header">
              <div className="secondary-panel-heading">
                {isMobile && renderMobileBackButton(t('chat.sidebar.backToMenu'))}
                <h2 className="workspaces-sidebar-title">{t('chat.sidebar.tools')}</h2>
              </div>
            </div>
            <div className="workspaces-sidebar-list">
              {TOOLS_SECONDARY_ITEMS.map((item)=>(<button key={item.id} type="button" className={`context-row ${toolsTabParam === item.id ? 'context-row--active' : ''}`} onClick={()=>{
            navigate(`/domains/${selectedSite.site_id}/tools?toolsTab=${item.id}`, {
                replace: true
            });
            if (isMobile) onMobileNavClose?.();
        }} aria-label={t(`chat.sidebar.toolsItems.${item.id}`)} aria-current={toolsTabParam === item.id ? 'page' : undefined}>
                  <div className="context-name-wrap">
                    <span className="context-name">{t(`chat.sidebar.toolsItems.${item.id}`)}</span>
                  </div>
                </button>))}
            </div>
          </div>
          <div className={`resize-handle ${isHistoryDragging ? 'resize-handle--dragging' : ''}`} onMouseDown={handleHistoryResizeStart} role="separator" aria-orientation="vertical"/>
        </div>)}

      {}
      {settingsSecondaryOpen && selectedSite && (<div className="sidebar-secondary-shell" style={isMobile ? undefined : {
        width: historySidebarWidth
    }}>
          <div className="workspaces-sidebar workspaces-sidebar--section">
            <div className="workspaces-sidebar-header">
              <div className="secondary-panel-heading">
                {isMobile && renderMobileBackButton(t('chat.sidebar.backToMenu'))}
                <h2 className="workspaces-sidebar-title">{t('chat.sidebar.settings')}</h2>
              </div>
            </div>
            <div className="workspaces-sidebar-list">
              {visibleSettingsItems.map((item)=>(<button key={item.id} type="button" className={`context-row ${settingsTabParam === item.id ? 'context-row--active' : ''}`} onClick={()=>{
            navigate(`/domains/${selectedSite.site_id}/settings?settingsTab=${item.id}`, {
                replace: true
            });
            if (isMobile) onMobileNavClose?.();
        }} aria-label={t(`chat.sidebar.settingsItems.${item.id}`)} aria-current={settingsTabParam === item.id ? 'page' : undefined}>
                  <div className="context-name-wrap">
                    <span className="context-name">{t(`chat.sidebar.settingsItems.${item.id}`)}</span>
                  </div>
                </button>))}
            </div>
          </div>
          <div className={`resize-handle ${isHistoryDragging ? 'resize-handle--dragging' : ''}`} onMouseDown={handleHistoryResizeStart} role="separator" aria-orientation="vertical"/>
        </div>)}

      {}
      {publishingSecondaryOpen && selectedSite && (<div className="sidebar-secondary-shell" style={isMobile ? undefined : {
        width: historySidebarWidth
    }}>
          <div className="workspaces-sidebar workspaces-sidebar--section">
            <div className="workspaces-sidebar-header">
              <div className="secondary-panel-heading">
                {isMobile && renderMobileBackButton(t('chat.sidebar.backToMenu'))}
                <h2 className="workspaces-sidebar-title">{t('chat.sidebar.publishing')}</h2>
              </div>
            </div>
            <div className="workspaces-sidebar-list">
              <button type="button" className={`context-row ${publishingTab === 'maps' ? 'context-row--active' : ''}`} onClick={()=>{
        navigate(`/domains/${selectedSite.site_id}/publishing?publishingTab=maps`, {
            replace: true
        });
        if (isMobile) onMobileNavClose?.();
    }} aria-label={t('chat.sidebar.maps')} aria-current={publishingTab === 'maps' ? 'page' : undefined}>
                <div className="context-name-wrap">
                  <span className="context-name">{t('chat.sidebar.maps')}</span>
                </div>
              </button>
              <button type="button" className="context-row" onClick={()=>{
        navigate(`${ROUTES.TEMPLATE_DESIGNER}/${selectedSite.site_id}`);
        if (isMobile) onMobileNavClose?.();
    }} aria-label={t('chat.sidebar.templateDesigner')}>
                <div className="context-name-wrap">
                  <span className="context-name">{t('chat.sidebar.templateDesigner')}</span>
                </div>
              </button>
            </div>
          </div>
          <div className={`resize-handle ${isHistoryDragging ? 'resize-handle--dragging' : ''}`} onMouseDown={handleHistoryResizeStart} role="separator" aria-orientation="vertical"/>
        </div>)}
    </div>);
};
export default AppSidebar;

import "macro-0ad44e244731debd57676975118ce7e54414faa9058a1470c1103dce60e4da26.css";
import "macro-ad17b40ec3e468ea81036784cea5b1555fb31833c8e0dee8500d71172058f0b6.css";
import "macro-89cc78f9ce01bfc664a571e6be65c132e7a70a6597add9ed867e3a7b9831582e.css";
import "macro-26332648629ae218f714e399b18c8bd2cb34b9d0ec40983e8e91fc8ea4e06222.css";
import "macro-26332648629ae218f714e399b18c8bd2cb34b9d0ec40983e8e91fc8ea4e06222.css";
import "macro-ad17b40ec3e468ea81036784cea5b1555fb31833c8e0dee8500d71172058f0b6.css";
import "macro-26332648629ae218f714e399b18c8bd2cb34b9d0ec40983e8e91fc8ea4e06222.css";
import "macro-26332648629ae218f714e399b18c8bd2cb34b9d0ec40983e8e91fc8ea4e06222.css";